<template>
  <span>
    <v-dialog v-model="dialog" width="600">
      <template v-slot:activator="{ on }">
        <v-btn icon v-on="on">
          <v-icon>{{activatorIcon ? activatorIcon:'mdi-folder-open-outline'}}</v-icon>
        </v-btn>
      </template>

      <v-card width="100%" class="mx-auto">
        <v-toolbar>
          <v-toolbar-title>Search Materials</v-toolbar-title>
          <v-btn class="ml-auto" icon @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-toolbar v-if="collapseSearch" collapse dense>
          <v-spacer/>
          Search
          <v-btn @click="collapseSearch = !collapseSearch" icon>
            <v-icon>mdi-export</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text v-else class="mt-5">
          <v-select
            outlined
            v-if="displayType"
            v-model="selectedType"
            label="Types"
            :items="materialTypes"
          ></v-select>
          <v-text-field
            :loading="isAppLoading"
            outlined
            :hide-details="true"
            label="Enter search terms"
            append-icon="mdi-text-search"
            v-model="term"
            @keydown.enter="searchMaterials"
            @click:append="searchMaterials"
          ></v-text-field>
          <v-select
            v-model="selectedGrade"
            :items="grades"
            item-text="name"
            item-value="value"
            label="Grade"
            clearable
          />
          <v-select v-model="selectedSubject" :items="subjects" label="Subject" clearable />
          <v-select v-model="selectedYear" :items="years" label="Year" clearable />

          <v-btn @click="searchMaterials">Search</v-btn>
        </v-card-text>

        <v-card class="mt-5" v-if="multiple && selectedResults.length > 0">
          <v-card-title>
            Selected {{selectedResults.length}} Topics
            <v-spacer />
            <v-btn @click="chooseSearchResult">Done with Selection</v-btn>
          </v-card-title>
        </v-card>

        <v-subheader>Found {{totalElements || 0}} results</v-subheader>
        <v-card-text v-if="searchResults && searchResults.length > 0">
          <v-list v-if="!multiple">
            <v-list-item-group v-model="selectedResource">
              <v-list-item v-for=" (row) in searchResults " :key="row.id">
                <v-list-item-content>
                  <v-list-item-title>{{row.name}}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list-item-group>
          </v-list>
          <div v-if="multiple">
            <v-text-field v-model="searchTerm" label="Filter results" clearable/>
            <v-treeview v-model="selectedResults" :search="searchTerm" :open-all="true" selectable :items="searchResults" >
              <template slot="label" slot-scope="{ item }">
                  <a v-if="item.imageUrl" @click="preview(item)">{{ item.name }}</a>
                  <span v-else>{{ item.name }}</span>
                </template>
            </v-treeview>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="previewImg" width="600">
      <v-card v-if="previewSrc">
        <v-card-title>
            <v-spacer/>
            <v-btn icon @click="previewImg=false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
        </v-card-title>
        <v-card-text>
          <img width="90%" :src="previewSrc" />
        </v-card-text>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
/**
 * @activatorIcon - controls activating icon.  use mdi-icon-name
 * @domain - controls the top parent directory to look for resources.  The value could be "global", "course", or "exam"
 * @includeTypes - Types of resources to include in the search results
 * @grade - Grade to filter the result
 * @subject - Subject to filter
 * @year - Year to filter the result (this is for exams)
 * @searchTerms - keyword to search for materials
 * @multiple - allow to select multiple resources
 * @courseId - When domain is 'course' and courseId is supplied, then only show materials under course
 * @course - When domain is 'course' and courseId is supplied, then only show materials under course
 * @materials - predefined set of materials, when this property is available, then server query will be skipped
 * @callbackProps - properties to pass back on emit event 'selectedFile'
 */

/**
 * Events:
 * selectedFile - when a file is selected, the selected file will be passed through the argument
 * onOpen - when the dialog opens
 */
import { mapGetters, mapActions } from "vuex";

export default {
  props: [
    "activatorIcon",
    "domain",
    "includeTypes",
    "grade",
    "subject",
    "year",
    "searchTerms",
    "results",
    "multiple",
    "courseId",
    "course",
    "materials",
    "topicsMaterials",
    "callbackProps"
  ],
  data() {
    return {
      dialog: false,
      previewImg: false,
      previewSrc: null,
      page: 1,
      pageLength: 0,
      pageSize: 5,
      searchResults: [],
      selectedResults: [],
      totalElements: 0,
      term: "",
      years: [2015, 2016, 2017, 2018, 2019, 2020],
      selectedGrade: null,
      selectedSubject: null,
      selectedYear: null,
      selectedResource: null,
      materialTypes: [
        "All",
        "Exam",
        "Question",
        "Passage",
        "Chart",
        "Image",
        "Formula"
      ],
      selectedType: null,
      displayType: true,
      mockResults: [{ id: 1, name: "passage 1", type: "passage" }],
      collapseSearch: false,
      searchTerm: null
    };
  },
  watch: {
    dialog(show) {
      if (show) {
        this.openDialog();
      }
    },
    selectedResource(value){
      if(value >= 0){
        const material = this.searchResults[value];
        this.$emit("selectedFile", material);
        this.dialog = false;
      }
    }
  },
  computed: {
    ...mapGetters(["navState", "isAppLoading", "grades", "subjects"]),
    pagedResult() {
      const start = (this.page - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.searchResults.slice(start, end);
    },
  },
  methods: {
    ...mapActions(["addErrorMessage", "addMessage"]),
    searchMaterials() {
      if (this.materials) {
        this.prepareCourseMaterials();
      } else {
        this.page = 1;
        const params = {
          searchTerm: this.term,
          grade: this.selectedGrade,
          subject: this.selectedSubject,
          year: this.selectedYear,
          pageSize: 10
        };

        if (this.domain == "course") {
          if (this.courseId) {
            this.searchCourseMaterials(this.courseId);
          } else {
            this.searchAllCourses();
          }
        } else if (this.domain == "exam") {
          this.listExams(params);
        }
      }
    },
    searchCourseMaterials(courseId) {
      console.log('search course materials')
      this.$axios
        .get("classroom/course", {
          params: { courseId: courseId }
        })
        .then(resp => {
          const course = resp.data[0];
          //extract materials
          const courseMaterials = course.courseMaterials;
          console.log('found course materials:')
          console.log(courseMaterials)
          if (courseMaterials && courseMaterials.length > 1) {
            this.searchResults = courseMaterials.map(m => {
              return { id: m.materialsId, name: m.materials.name };
            });

          }
        })
        .catch(() => {
          this.addErrorMessage("Error loading course from server");
        });
    },
    searchAllCourses() {
      const searchRequest = {
        filterTerm: {
          COURSE: [
            { name: "grade", value: this.grade },
            { name: "subject", value: this.subject },
            { name: "includeArchived", value: false }
          ]
        },
        searchTerm: null,
        searchEntities: ["COURSE"]
      };
      this.$axios
        .post("/classroom/search-courses", searchRequest)
        .then(resp => {
          const courses = resp.data.content;
          if (courses && courses.length > 0) {
            this.searchResults = courses.map(c => {
              let children = [];
              if (c.courseMaterials && c.courseMaterials.length > 1) {
                children = c.courseMaterials.map(m => {
                  return { id: m.materialsId, name: m.materials.name };
                });
              }
              return { name: c.name, id: c.id, children };
            });
          }
        })
        .catch(() => {
          this.addErrorMessage("Error loading courses from server");
        });
    },
    prepareCourseMaterials(){
      //gather all materials already associated with topics
      let categorizedMaterialIds = [];
      let categorizedMaterials = [];
      this.topicsMaterials.forEach(tm => {
        const mIds = tm.materials.map( m => m.materials.id );
        categorizedMaterialIds = categorizedMaterialIds.concat(mIds);

        const tChildren = tm.materials.map(m => {return {id: `${tm.id}-${m.materials.id}`, imageUrl: m.materials.imageUrl, thumbnailUrl: m.materials.thumbnailImageUrl, materialId: m.materials.id, name: m.materials.name} });
        if(tChildren && tChildren.length > 0){
          categorizedMaterials.push({id: `t-${tm.id}`, name: tm.name, children: tChildren})
        }
      });


      const unCategorizedMaterials = this.materials.filter( m => !categorizedMaterialIds.includes(m.materials.id));

      const children = unCategorizedMaterials.map( m => { return { id: `0-${m.materialsId}`, imageUrl: m.materials.imageUrl, thumbnailUrl: m.materials.thumbnailImageUrl, materialId: m.materials.id, name: m.materials.name } })
      const unCats = []
      if(children && children.length > 0){
        unCats.push({ id: 't-0', name: 'Not Categorized', children });
      }
      this.totalElements = children.length;
      this.searchResults = [{ id: `c_${this.course.id}`, name: this.course.name, children: [...unCats, ...categorizedMaterials]}]
      this.collapseSearch = true;
    },
    listExams(params) {
      this.$axios
        .get("/resource-mgmt/list-exams", { params })
        .then(resp => {
          this.searchResults = resp.data.data || [];
          this.totalElements = this.searchResults.length;
          this.pageLength =
            Math.ceil(this.totalElements / this.pageSize) || 0;
          this.collapseSearch = true;
        })
        .catch(resp => {
          this.addErrorMessage(resp.data.message || resp.data);
        });
    },
    searcQuestionhReferences() {
      //Replace with api call
      return this.mockResults;
    },
    openDialog(event) {
      this.$emit("onOpen", event);
    },
    chooseSearchResult() {
      
      const selectedMaterialIds = this.selectedResults.map( r => parseInt(r.split('-')[1]));
      const selectedMaterials = this.materials.filter(m => selectedMaterialIds.includes(m.materials.id));
      this.$emit("selectedFile", selectedMaterials, this.callbackProps);

      this.dialog = false;
    },
    preview(item){
      if(item.imageUrl){
        const src = `${process.env.VUE_APP_BASE_URL}/s3/get?path=${item.imageUrl}`;
        this.previewImg = true;
        this.previewSrc = src;
      }
    }
  },
  mounted() {
    this.term = this.searchTerms;
    this.selectedGrade = this.grade;
    this.selectedSubject = this.subject;
    this.selectedYear = this.year;

    if (this.includeTypes && this.includeTypes.length == 1) {
      this.displayType = false;
      this.selectedType = this.includeTypes[0];
    } else if (this.includeTypes && this.includeTypes.length > 1) {
      this.displayType = true;
      this.materialTypes = this.materialTypes.filter(
        t => this.includeTypes.indexOf(t) >= 0
      );
    }
    this.searchMaterials();
  },
};
</script>

<style>
</style>